import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const services = [
  {
    num: '01',
    icon: 'ri-home-4-line',
    title: 'Residential Settlements',
    desc: 'We guide home buyers and sellers through every step of the closing process in Bucks, Montgomery, Chester, and Delaware County, PA. Our team coordinates with lenders, realtors, and all parties to ensure a smooth, on-time closing.',
    details: ['Purchase & sale closings', 'Coordination with lenders & agents', 'HUD-1 / Closing Disclosure review', 'Same-day recording available', 'Evening & weekend closings by appointment'],
  },
  {
    num: '02',
    icon: 'ri-building-2-line',
    title: 'Commercial Closings',
    desc: 'Complex commercial real estate transactions require experienced professionals. Black Dog Settlement handles commercial closings of all sizes across southeastern Pennsylvania with precision and efficiency.',
    details: ['Office, retail & industrial properties', 'Multi-unit residential buildings', 'Land & development transactions', 'Complex lien resolution', 'Coordination with commercial lenders'],
  },
  {
    num: '03',
    icon: 'ri-shield-check-line',
    title: 'Title Insurance',
    desc: 'Protect your investment with comprehensive owner and lender title insurance policies. We work with leading underwriters to provide robust coverage against hidden title defects, liens, and ownership disputes.',
    details: ["Owner's title insurance policies", "Lender's title insurance policies", 'Coverage for hidden defects & liens', 'Forgery & fraud protection', 'Lifetime coverage for as long as you own'],
  },
  {
    num: '04',
    icon: 'ri-search-eye-line',
    title: 'Title Searches',
    desc: 'Our thorough title searches examine public records going back decades to verify clear ownership and uncover any liens, encumbrances, judgments, or other issues before closing.',
    details: ['Full chain of title examination', 'Lien & judgment searches', 'Tax search & verification', 'Easement & restriction review', 'Municipal lien certificates'],
  },
  {
    num: '05',
    icon: 'ri-exchange-line',
    title: '1031 Exchanges',
    desc: 'Defer capital gains taxes by exchanging investment properties under IRS Section 1031. Our experienced team coordinates with qualified intermediaries to ensure full compliance and a smooth exchange process.',
    details: ['Coordination with QI (Qualified Intermediary)', 'Strict timeline management', 'Like-kind property guidance', 'Multi-property exchanges', 'Reverse & improvement exchanges'],
  },
  {
    num: '06',
    icon: 'ri-bank-line',
    title: 'Refinancing Services',
    desc: 'Streamline your refinance closing with Black Dog Settlement. We work efficiently with your lender to review loan documents, conduct title searches, and close your refinance quickly and accurately.',
    details: ['Rate & term refinances', 'Cash-out refinances', 'Title search & insurance', 'Lender coordination', 'Fast turnaround times'],
  },
  {
    num: '07',
    icon: 'ri-file-text-line',
    title: 'Deed Preparation',
    desc: 'Accurate deed preparation is critical to a valid property transfer. Our team prepares and records all types of deeds in compliance with Pennsylvania law and county recording requirements.',
    details: ['Warranty deeds', 'Quitclaim deeds', "Sheriff's deeds", 'Corrective deeds', 'County recording & filing'],
  },
  {
    num: '08',
    icon: 'ri-file-search-line',
    title: 'Lien Searches',
    desc: "Identify any outstanding claims against a property before closing. Our comprehensive lien searches cover federal and state tax liens, judgment liens, mechanic's liens, and more.",
    details: ['Federal & state tax lien searches', 'Judgment lien searches', "Mechanic's lien searches", 'HOA lien verification', 'Municipal utility lien searches'],
  },
];

const faqs = [
  { q: 'What counties does Black Dog Settlement serve?', a: 'We proudly serve Bucks County, Montgomery County, Chester County, and Delaware County in Pennsylvania, with our office located in Newtown, PA.' },
  { q: 'Do you offer title insurance in Pennsylvania?', a: "Yes. We provide both owner's and lender's title insurance policies for residential and commercial properties throughout southeastern Pennsylvania, protecting your investment from hidden defects and liens." },
  { q: 'How long does a residential settlement take?', a: 'Most residential closings take 1–2 hours. We prepare all documents in advance so closing day is smooth and efficient for all parties.' },
  { q: 'Can you handle 1031 exchanges in Bucks County?', a: 'Absolutely. We coordinate 1031 tax-deferred exchanges for investment property owners across Bucks, Montgomery, Chester, and Delaware Counties, working closely with qualified intermediaries.' },
  { q: 'What is a title search and why do I need one?', a: 'A title search examines public records to verify clear ownership of a property and identify any liens, judgments, or encumbrances. It is a critical step in any real estate transaction to protect buyers and lenders.' },
];

const ServicesPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <head>
        <title>Title &amp; Settlement Services | Black Dog Settlement Bucks Montgomery Chester Delaware County PA</title>
        <meta name="description" content="Black Dog Settlement offers residential settlements, commercial closings, title insurance, title searches, 1031 exchanges, refinancing, deed preparation, and lien searches in Bucks, Montgomery, Chester, and Delaware County, PA." />
        <meta name="keywords" content="title insurance Bucks County PA, residential settlement Montgomery County, commercial closing Chester County, 1031 exchange Delaware County Pennsylvania" />
        <link rel="canonical" href="https://www.blackdogsettlement.com/services" />
        <meta name="geo.region" content="US-PA" />
        <meta name="geo.placename" content="Newtown, Pennsylvania" />
        <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700;800;900&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </head>
      <div style={{ fontFamily: "'Inter', sans-serif" }}>
        <Navbar />

        {/* Hero */}
        <section className="relative w-full min-h-[60vh] flex flex-col lg:flex-row overflow-hidden">
          <div className="relative z-10 w-full lg:w-[55%] bg-[#0f0f0f] flex flex-col justify-end px-8 md:px-16 lg:px-20 pt-36 pb-16 lg:pb-20">
            <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-[#c9a84c]/20 to-transparent hidden lg:block"></div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-px bg-[#c9a84c]"></div>
              <span className="text-[#c9a84c] text-xs font-bold tracking-[0.3em] uppercase">Our Services</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-white leading-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Comprehensive<br />Title &amp; Settlement<br /><span className="text-[#c9a84c]">Services.</span>
            </h1>
            <p className="text-white/40 text-sm leading-relaxed max-w-md">
              From residential closings to commercial transactions, Black Dog Settlement delivers expert title and settlement services across Bucks, Montgomery, Chester, and Delaware County, PA.
            </p>
          </div>
          <div className="w-full lg:w-[45%] h-[40vh] lg:h-auto">
            <img
              src="https://storage.readdy-site.link/project_files/24cbc93a-97c5-4a4c-8795-e638165a0b41/2bd7f284-56d1-42b7-92ed-b25d53f06fd8_shutterstock_2696800907.jpg?v=8beaa771ff7093274168911ba9fff4a4"
              alt="Title and settlement services in Bucks Montgomery Chester Delaware County Pennsylvania"
              className="w-full h-full object-cover object-center"
            />
          </div>
        </section>

        {/* Services list */}
        <section className="bg-white py-0">
          <div className="border-t border-stone-100">
            {services.map((svc, i) => (
              <div key={svc.num} className="border-b border-stone-100">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row gap-8">
                  {/* Left */}
                  <div className="flex items-start gap-6 md:w-[45%]">
                    <span className="text-xs font-bold tracking-widest text-stone-300 w-8 shrink-0 mt-1">{svc.num}</span>
                    <div className="w-12 h-12 flex items-center justify-center border border-stone-100 shrink-0">
                      <i className={`${svc.icon} text-xl text-[#c9a84c]`}></i>
                    </div>
                    <div>
                      <h2 className="text-xl font-black text-[#0f0f0f] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{svc.title}</h2>
                      <p className="text-stone-400 text-sm leading-relaxed">{svc.desc}</p>
                    </div>
                  </div>
                  {/* Right - details */}
                  <div className="md:w-[55%] md:pl-8 md:border-l border-stone-100">
                    <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-300 mb-4">Includes</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {svc.details.map((d) => (
                        <div key={d} className="flex items-center gap-2 text-sm text-stone-500">
                          <div className="w-4 h-4 flex items-center justify-center shrink-0">
                            <i className="ri-check-line text-[#c9a84c] text-xs"></i>
                          </div>
                          {d}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-[#f7f5f0] py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col lg:flex-row gap-16">
              <div className="w-full lg:w-[35%]">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-px bg-[#c9a84c]"></div>
                  <span className="text-[#c9a84c] text-xs font-bold tracking-[0.3em] uppercase">FAQ</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-black text-[#0f0f0f] leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Common<br />Questions
                </h2>
              </div>
              <div className="w-full lg:w-[65%] space-y-0 border-t border-stone-200">
                {faqs.map((faq, i) => (
                  <div key={i} className="border-b border-stone-200">
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between py-6 text-left cursor-pointer group"
                    >
                      <span className={`font-bold text-sm pr-8 transition-colors ${openFaq === i ? 'text-[#c9a84c]' : 'text-[#0f0f0f] group-hover:text-[#c9a84c]'}`}>
                        {faq.q}
                      </span>
                      <div className={`w-8 h-8 flex items-center justify-center border shrink-0 transition-all ${openFaq === i ? 'border-[#c9a84c] bg-[#c9a84c] text-[#0f0f0f]' : 'border-stone-200 text-stone-400'}`}>
                        <i className={`text-sm transition-transform ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                      </div>
                    </button>
                    {openFaq === i && (
                      <p className="pb-6 text-stone-500 text-sm leading-relaxed">{faq.a}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#0f0f0f] py-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-4xl font-black text-white mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ready to Schedule Your Settlement?</h2>
              <p className="text-white/40 text-sm">Contact Black Dog Settlement today. We serve Bucks, Montgomery, Chester, and Delaware County, PA.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <a
                href="tel:2154990796"
                className="inline-flex items-center justify-center gap-2 bg-[#c9a84c] hover:bg-[#b8963e] text-[#0f0f0f] font-bold px-8 py-4 text-xs tracking-[0.15em] uppercase whitespace-nowrap cursor-pointer transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line"></i>
                </div>
                Call 215-499-0796
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/50 text-white font-bold px-8 py-4 text-xs tracking-[0.15em] uppercase whitespace-nowrap cursor-pointer transition-colors"
              >
                Send a Message
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default ServicesPage;
